import { zodResolver } from '@hookform/resolvers/zod';
import { ColumnDef } from '@tanstack/react-table';
import { Edit, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import Header from '../components/Header';
import { Badge } from '../components/ui/badge';
import { ConfirmDialog } from '../components/ui/alert-dialog-helper';
import { Button } from '../components/ui/button';
import { createLinkColumn } from '../components/ui/column-helpers';
import { DataTable } from '../components/ui/data-table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

interface Supplier {
  id: string;
  name: string;
  is_active: boolean;
  notes: string | null;
  product_count: number;
  margin_tiers_configured: number;
  created_at: string;
}

interface SupplierStats {
  total: number;
  active: number;
  linkedProducts: number;
}

const supplierSchema = z.object({
  name: z.string().min(1, 'Name is required').max(200),
  notes: z.string().max(2000).optional(),
  is_active: z.boolean().optional(),
});

type SupplierForm = z.infer<typeof supplierSchema>;

const token = () => localStorage.getItem('token');

export default function Suppliers() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [stats, setStats] = useState<SupplierStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState<Supplier | null>(null);

  const form = useForm<SupplierForm>({
    resolver: zodResolver(supplierSchema),
    defaultValues: { name: '', notes: '', is_active: true },
  });

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (!token() || !savedUser) { navigate('/login'); return; }
    try { setUser(JSON.parse(savedUser)); } catch { navigate('/login'); }
    loadSuppliers();
    loadStats();
  }, []);

  const loadSuppliers = async () => {
    try {
      const res = await fetch('/api/suppliers?include_inactive=true', { headers: { Authorization: `Bearer ${token()}` } });
      if (res.ok) {
        const data = await res.json();
        setSuppliers(data.success ? data.data : data);
      }
    } catch (err) {
      console.error('Error loading suppliers:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await fetch('/api/suppliers/stats', { headers: { Authorization: `Bearer ${token()}` } });
      const data = await res.json();
      if (data.success) setStats(data.data);
    } catch (err) {
      console.error('Error loading supplier stats:', err);
    }
  };

  const startCreate = () => {
    setEditing(null);
    form.reset({ name: '', notes: '', is_active: true });
    setDialogOpen(true);
  };

  const startEdit = (supplier: Supplier) => {
    setEditing(supplier);
    form.reset({ name: supplier.name, notes: supplier.notes || '', is_active: supplier.is_active });
    setDialogOpen(true);
  };

  const onSubmit = async (values: SupplierForm) => {
    setSubmitting(true);
    try {
      const url = editing ? `/api/suppliers/${editing.id}` : '/api/suppliers';
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify(values),
      });
      if (res.ok) {
        setDialogOpen(false);
        form.reset();
        await Promise.all([loadSuppliers(), loadStats()]);
      }
    } catch (err) {
      console.error('Error saving supplier:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteSupplier = async () => {
    if (!supplierToDelete) return;
    try {
      const res = await fetch(`/api/suppliers/${supplierToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.ok) {
        setDeleteDialogOpen(false);
        setSupplierToDelete(null);
        await Promise.all([loadSuppliers(), loadStats()]);
      }
    } catch (err) {
      console.error('Error deleting supplier:', err);
    }
  };

  const canEdit = user?.roles?.some(r => ['admin', 'supervisor'].includes(r.key)) ?? false;
  const canDelete = user?.roles?.some(r => r.key === 'admin') ?? false;

  const columns: ColumnDef<Supplier>[] = [
    createLinkColumn<Supplier>('name', 'Name', (supplier) => navigate(`/suppliers/${supplier.id}`)),
    {
      accessorKey: 'product_count',
      header: 'Products',
      cell: ({ row }) => <span className="font-medium">{row.getValue('product_count')}</span>,
    },
    {
      accessorKey: 'margin_tiers_configured',
      header: 'Margin Tiers',
      cell: ({ row }) => {
        const count = row.getValue('margin_tiers_configured') as number;
        return count > 0
          ? <Badge variant="secondary">{count} configured</Badge>
          : <span className="text-muted-foreground text-sm">Not configured</span>;
      },
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.getValue('is_active') ? 'default' : 'secondary'}>
          {row.getValue('is_active') ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ];

  if (canEdit) {
    columns.push({
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => startEdit(row.original)}>
              <Edit className="h-4 w-4 mr-2" /> Edit
            </DropdownMenuItem>
            {canDelete && (
              <DropdownMenuItem className="text-destructive" onClick={() => { setSupplierToDelete(row.original); setDeleteDialogOpen(true); }}>
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    });
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="suppliers" />
      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-heading font-bold">Suppliers</h1>
          {canEdit && (
            <Button onClick={startCreate}>
              <Plus className="h-4 w-4 mr-2" /> Add Supplier
            </Button>
          )}
        </div>

        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card><CardContent className="pt-6"><div className="text-sm text-muted-foreground">Total Suppliers</div><div className="text-2xl font-bold">{stats.total}</div></CardContent></Card>
            <Card><CardContent className="pt-6"><div className="text-sm text-muted-foreground">Active Suppliers</div><div className="text-2xl font-bold">{stats.active}</div></CardContent></Card>
            <Card><CardContent className="pt-6"><div className="text-sm text-muted-foreground">Products Linked</div><div className="text-2xl font-bold">{stats.linkedProducts}</div></CardContent></Card>
          </div>
        )}

        <DataTable columns={columns} data={suppliers} loading={loading} />

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Supplier' : 'Add Supplier'}</DialogTitle>
              <DialogDescription>{editing ? 'Update supplier details.' : 'Create a new supplier.'}</DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField control={form.control} name="name" render={({ field }) => (
                  <FormItem><FormLabel>Name</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="notes" render={({ field }) => (
                  <FormItem><FormLabel>Notes</FormLabel><FormControl><Textarea {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="is_active" render={({ field }) => (
                  <FormItem className="flex items-center gap-2">
                    <FormControl><input type="checkbox" checked={field.value} onChange={field.onChange} className="h-4 w-4" /></FormControl>
                    <FormLabel className="!mt-0">Active</FormLabel>
                  </FormItem>
                )} />
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={submitting}>{editing ? 'Save Changes' : 'Create Supplier'}</Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        <ConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          title="Delete Supplier"
          description={`Are you sure you want to delete "${supplierToDelete?.name}"? Products linked to this supplier will be unlinked.`}
          onConfirm={deleteSupplier}
          confirmText="Delete"
          variant="destructive"
        />
      </main>
    </div>
  );
}
